import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, Save, User, FileSignature, History, Link2, Check, BarChart3, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { getSettings } from "../lib/api";
import { autoSplit } from "../lib/nameSplit";
import { useAuth } from "../lib/AuthContext";
import { SeasonsEditor } from "./SeasonsEditor";
import { TEAM_INFO } from "../lib/teamMap";
import { PLAYER_LEAGUE_OPTIONS, teamsForLeagueName } from "../lib/allTeams";
import { PlayerAvatar } from "./PlayerAvatar";
import { TeamBadge } from "./TeamBadge";
import { PhotoCropAdjust } from "./admin/PhotoCropAdjust";
import { DatePicker } from "./DatePicker";
import { ImageUpload } from "./ImageUpload";
import { parseFlexibleDate } from "../lib/date";

const POSITIONS = ["LW", "C", "RW", "W", "F", "LD", "RD", "D", "G"];
const inp = "w-full bg-[#0A0B10] border border-[#1E2233] rounded-md text-white text-sm px-2.5 py-1.5 outline-none focus:border-[#9D4CDD] transition-colors";
const lbl = "block text-[10px] uppercase tracking-wider font-bold text-white/45 mb-1";

// Pinned league order; everything else follows, sorted by number of players.
const PINNED_LEAGUES = ["DNP", "PWHL", "SDHL", "NCAA", "USports", "NDHL"];
const orderedLeagues = (allPlayers) => {
    const counts = {};
    for (const p of allPlayers || []) { const lg = p.league || (p.active === false ? "DNP" : ""); if (lg) counts[lg] = (counts[lg] || 0) + 1; }
    const pinned = PINNED_LEAGUES.filter((l) => PLAYER_LEAGUE_OPTIONS.includes(l));
    const rest = PLAYER_LEAGUE_OPTIONS.filter((l) => !pinned.includes(l)).sort((a, b) => (counts[b] || 0) - (counts[a] || 0) || a.localeCompare(b));
    return [...pinned, ...rest];
};
const cmToFtIn = (cm) => { const n = Number(cm) || 0; if (n <= 0) return ""; const t = Math.round(n / 2.54); return `${Math.floor(t / 12)}'${t % 12}"`; };
const ageFrom = (bd) => { const d = parseFlexibleDate(bd); if (!d) return null; const t = new Date(); let a = t.getFullYear() - d.getFullYear(); const m = t.getMonth() - d.getMonth(); if (m < 0 || (m === 0 && t.getDate() < d.getDate())) a -= 1; return a >= 0 && a < 120 ? a : null; };

const SECTIONS = [
    { k: "bio", l: "Player Bio", Icon: User },
    { k: "logs", l: "Game Logs", Icon: BarChart3 },
    { k: "contracts", l: "Contracts", Icon: FileSignature },
    { k: "history", l: "History & Awards", Icon: History },
];

const numify = (rows) => (rows || []).map((r) => {
    const o = { ...r };
    Object.keys(o).forEach((k) => {
        if (typeof o[k] === "string" && o[k] !== "" && !isNaN(o[k]) && !["season", "team", "team_name", "league", "stage", "class", "club"].includes(k)) o[k] = Number(o[k]);
    });
    return o;
});

// Name corrector: click a gap between words to set where the first name ends (e.g. Kendall | Coyne Schofield).
function NameCorrector({ name, first, onSplit }) {
    const words = String(name || "").trim().split(/\s+/).filter(Boolean);
    if (words.length < 3) return null;
    const cur = first ? first.trim().split(/\s+/).filter(Boolean).length : words.length - 1;
    return (
        <div className="col-span-3 flex flex-wrap items-center gap-1" data-testid="pe-name-corrector">
            <span className="text-[10px] uppercase tracking-wider font-bold text-white/45 mr-1">First | Last</span>
            {words.map((w, i) => (
                <React.Fragment key={i}>
                    {i > 0 && <button type="button" onClick={() => onSplit(i)} title="Split first / last name here" data-testid={`pe-name-split-${i}`}
                        className={`h-6 w-3 rounded transition-colors ${i === cur ? "bg-[#9D4CDD]" : "bg-white/5 hover:bg-white/20"}`} />}
                    <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${i < cur ? "text-white/60 bg-white/5" : "text-white bg-[#9D4CDD]/25"}`}>{w}</span>
                </React.Fragment>
            ))}
        </div>
    );
}

function Field({ label, children }) {
    return <label className="block"><span className={lbl}>{label}</span>{children}</label>;
}

// Team selector that adapts to the chosen league (PWHL/NCAA/other/free-text).
function TeamPicker({ league, value, onChange }) {
    if (league === "DNP") {
        return <div className="text-[11px] text-white/50 bg-[#0A0B10] border border-[#1E2233] rounded-md px-2.5 py-1.5" data-testid="pe-team-dnp">No team — Inactive</div>;
    }
    const opts = teamsForLeagueName(league);
    if (opts.length === 0) {
        return <input value={value || ""} onChange={(e) => onChange(e.target.value)} className={inp} placeholder="e.g. Canada" data-testid="pe-team" />;
    }
    return (
        <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={inp} data-testid="pe-team">
            <option value="">— Select team —</option>
            {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
    );
}

// 6-item completion checklist pills (Bio · Hist · Logs · Photo · EP · Awards).
function ChecklistBar({ p, f }) {
    const items = [
        { ok: !!p.bio_complete, label: "Bio" },
        { ok: !!p.career_complete, label: "Hist" },
        { ok: p.logs_complete == null ? null : !!p.logs_complete, label: "Logs" },
        { ok: !!f.photo_url, label: "Photo" },
        { ok: !!f.eliteprospects_url, label: "EP" },
    ];
    const done = items.filter((i) => i.ok === true).length;
    return (
        <div className="flex items-center gap-1.5" data-testid="pe-checklist">
            <span className="text-[10px] font-black font-mono-stat text-white/60">{done}/{items.length}</span>
            {items.map((it) => {
                const cls = it.ok === null ? "bg-[#1E2233] text-[#64748B] border-[#334155]/60"
                    : it.ok ? "bg-[#22C55E]/20 text-[#86EFAC] border-[#22C55E]/50"
                    : "bg-[#EF4444]/10 text-[#FCA5A5] border-[#EF4444]/40";
                return (
                    <span key={it.label} data-testid={`pe-check-${it.label.toLowerCase()}`}
                        className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border ${cls}`}>
                        {it.ok === null ? "–" : it.ok ? <Check className="h-2.5 w-2.5" /> : <X className="h-2.5 w-2.5" />} {it.label}
                    </span>
                );
            })}
        </div>
    );
}

// "2023-r4-p5" → { year:"2023", round:"4", pick:"5" }
function parseDraftPick(id) {
    if (!id) return null;
    const m = String(id).match(/(\d{4})-r(\d+)-p(\d+)/i);
    return m ? { year: m[1], round: m[2], pick: m[3] } : { raw: String(id) };
}

// Collapse the per-season contract rows into ONE row per signed contract
// (same signing date + term + team), listing every season it covers.
function groupContracts(contracts) {
    const groups = {};
    for (const c of contracts || []) {
        const key = `${c.date_signed || ""}|${c.term || ""}|${c.team_id || ""}|${c.status || ""}`;
        if (!groups[key]) groups[key] = { ...c, seasons: [] };
        if (c.season) groups[key].seasons.push(c.season);
    }
    return Object.values(groups)
        .map((g) => ({ ...g, seasons: [...new Set(g.seasons)].sort() }))
        .sort((a, b) => (b.date_signed || "").localeCompare(a.date_signed || ""));
}

export default function PlayerQuickEditor({ player, teamId, onClose }) {
    const qc = useQueryClient();
    const navigate = useNavigate();
    const { isAdmin } = useAuth();
    const isNew = !player.id;
    const [section, setSection] = useState("bio");
    const [delOpen, setDelOpen] = useState(false);
    const [delText, setDelText] = useState("");
    const [createConfirm, setCreateConfirm] = useState(false);
    const [f, setF] = useState({
        name: player.name || "", first_name: player.first_name || "", last_name: player.last_name || "", jersey: player.jersey ?? "", position: player.position || "",
        position_manual: player.position_manual ?? !((player.stats?.pwhl || []).length),
        auto_position: player.auto_position || "", pref_position: player.pref_position || "",
        league: player.league || "PWHL",
        team_id: player.team_id || teamId, shoots: player.shoots || "", height_cm: player.height_cm ?? "",
        birthdate: player.birthdate || "", nationality: player.nationality || "", birthplace: player.birthplace || "",
        eliteprospects_url: player.eliteprospects_url || "", photo_url: player.photo_url || "",
        photo_x: player.photo_x ?? 50, photo_y: player.photo_y ?? 50, photo_zoom: player.photo_zoom ?? 1,
        contract_status: player.contract_status || "", contract_start_season: player.contract_start_season || "",
        contract_end_season: player.contract_end_season || "", current_salary: player.current_salary ?? "",
        class_year: player.class_year || "", entry_method: player.entry_method || "", entry_specify: player.entry_specify || "",
        retirement_type: player.retirement_type || "", retirement_season: player.retirement_season || "",
        hall_of_fame: !!player.hall_of_fame,
        is_staff: !!player.is_staff, staff_role: player.staff_role || "",
        pwhl: player.stats?.pwhl || [], ncaa: player.stats?.ncaa || [],
        international: player.stats?.international || [], other: player.stats?.other || [],
        awards: player.awards || [],
    });
    const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: getSettings });
    const { data: allPlayers = [] } = useQuery({ queryKey: ["admin-players"], queryFn: () => api.get("/admin/players").then((r) => r.data), staleTime: 60000 });
    const leagueOpts = orderedLeagues(allPlayers);
    const expansionNow = !!settings?.expansion_now;
    const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
    const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));
    const contracts = player.contracts || [];
    const groupedContracts = groupContracts(contracts);
    const draft = parseDraftPick(player.entry_draft_pick_id);
    const isDraft = player.entry_method === "Entry Draft";

    // --- Elite Prospects link & sync (server-side; fills only fields EP returns) ---
    const [epSyncing, setEpSyncing] = useState(false);
    const [epMsg, setEpMsg] = useState(null);
    const runEPSync = async () => {
        const url = (f.eliteprospects_url || "").trim();
        if (!/eliteprospects\.com\/player\/\d+/.test(url)) {
            setEpMsg({ type: "err", text: "Paste a valid Elite Prospects player URL." });
            return;
        }
        setEpSyncing(true); setEpMsg(null);
        try {
            const { data } = await api.post("/admin/players/eliteprospects-sync", { url });
            const facts = data.facts || {};
            setF((s) => ({
                ...s,
                eliteprospects_url: url,
                name: s.name || facts.name || "",
                jersey: facts.jersey || s.jersey,
                position: facts.position || s.position,
                pref_position: facts.pref_position || s.pref_position,
                shoots: facts.shoots || s.shoots,
                height_cm: facts.height_cm || s.height_cm,
                birthplace: facts.birthplace || s.birthplace,
                nationality: facts.nationality || s.nationality,
                birthdate: facts.birthdate || s.birthdate,
            }));
            setEpMsg({ type: "ok", text: `Synced ${data.stats?.length || 0} season rows + facts. Review, then Save.` });
        } catch (e) {
            setEpMsg({ type: "err", text: e?.response?.data?.detail || "Sync failed. EP may be unreachable." });
        } finally {
            setEpSyncing(false);
        }
    };

    const save = useMutation({
        mutationFn: () => {
            const [af, al] = autoSplit(f.name);
            const manualSplit = f.first_name && f.last_name && !(f.first_name === af && f.last_name === al);
            const body = {
                ...f,
                first_name: manualSplit ? f.first_name.trim() : "",
                last_name: manualSplit ? f.last_name.trim() : "",
                jersey: f.jersey === "" ? null : f.jersey,
                height_cm: f.height_cm === "" ? null : Number(f.height_cm),
                current_salary: f.current_salary === "" ? null : Number(f.current_salary),
                pwhl: numify(f.pwhl), ncaa: numify(f.ncaa), international: numify(f.international), other: numify(f.other),
                awards: (f.awards || []).filter((a) => (a || "").trim()),
            };
            return (isNew ? api.post(`/admin/players`, body) : api.put(`/admin/players/${player.id}`, body)).then((r) => r.data);
        },
        onSuccess: (data) => {
            qc.invalidateQueries({ queryKey: ["team-players-all", teamId] });
            qc.invalidateQueries({ queryKey: ["admin-players"] });
            qc.invalidateQueries({ queryKey: ["name-splits"] });
            toast.success(isNew ? "Player created" : "Player saved");
            onClose();
            if (isNew && data?.id) navigate(`/players/${data.id}`);
        },
        onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
    });

    const del = useMutation({
        mutationFn: () => api.delete(`/admin/players/${player.id}`).then((r) => r.data),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["team-players-all", teamId] });
            qc.invalidateQueries({ queryKey: ["admin-players"] });
            toast.success("Player deleted");
            onClose();
            navigate("/");
        },
        onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
    });

    const isGoalie = (f.position || player.position || "").toUpperCase() === "G";

    const autoPos = f.auto_position || f.pref_position || "";

    return (
        <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 pt-6 sm:pt-10 overflow-y-auto" onClick={onClose} data-testid="player-quick-editor">
            <div className="w-full max-w-5xl rounded-2xl border border-white/10 bg-[#0B0C12] shadow-2xl" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-center gap-3 px-5 py-3 border-b border-white/[0.06]">
                    <PlayerAvatar photo={f.photo_url} name={f.name} playerId={player.id} size="md" showTierRing={false} photoX={f.photo_x} photoY={f.photo_y} photoZoom={f.photo_zoom} />
                    <div className="min-w-0">
                        <h3 className="font-heading text-lg font-black text-white truncate">{f.name || "Player"}</h3>
                        <p className="text-[11px] text-white/45">{(TEAM_INFO[f.team_id]?.name || f.team_id || f.league)} · {f.position || "—"} · #{f.jersey || "—"}</p>
                    </div>
                    <div className="ml-auto flex items-center gap-3">
                        <div className="hidden sm:block"><ChecklistBar p={player} f={f} /></div>
                        <button onClick={onClose} data-testid="player-editor-close" className="h-8 w-8 rounded-lg flex items-center justify-center text-white/50 hover:text-white hover:bg-white/5"><X className="h-4 w-4" /></button>
                    </div>
                </div>

                {/* Section toggle */}
                <div className="flex items-center gap-1 px-5 pt-4">
                    {SECTIONS.map(({ k, l, Icon }) => (
                        <button key={k} onClick={() => setSection(k)} data-testid={`player-editor-tab-${k}`}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${section === k ? "bg-[#9D4CDD] text-white" : "bg-white/[0.04] text-white/60 hover:text-white"}`}>
                            <Icon className="h-3.5 w-3.5" /> {l}
                        </button>
                    ))}
                </div>

                {/* Section body */}
                <div className="p-5">
                    {section === "bio" && (
                        <div className="space-y-4" data-testid="player-editor-bio">
                            {/* Elite Prospects link & sync */}
                            <div className="rounded-lg border border-[#1E2233] bg-[#0A0B10] p-3 flex items-end gap-2 flex-wrap" data-testid="pe-ep-sync-bar">
                                <div className="flex-1 min-w-[240px]">
                                    <span className={lbl}>Elite Prospects URL</span>
                                    <input value={f.eliteprospects_url} onChange={set("eliteprospects_url")} className={inp} placeholder="https://www.eliteprospects.com/player/…" data-testid="pe-ep" />
                                </div>
                                <button type="button" onClick={runEPSync} disabled={epSyncing} data-testid="pe-ep-sync-btn"
                                    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-[11px] uppercase tracking-wider font-bold bg-[#9D4CDD] text-white hover:bg-[#8B3FC9] disabled:opacity-50 transition-colors">
                                    <Link2 className="h-3.5 w-3.5" /> {epSyncing ? "Syncing…" : "Link & Sync"}
                                </button>
                                {epMsg && <p className={`w-full text-[11px] font-semibold ${epMsg.type === "ok" ? "text-[#34D399]" : "text-[#F87171]"}`} data-testid="pe-ep-msg">{epMsg.text}</p>}
                            </div>

                            <div className="grid grid-cols-3 gap-2.5" data-testid="pe-name-boxes">
                                <Field label="Name"><input value={f.name} onChange={(e) => setF((st) => ({ ...st, name: e.target.value, first_name: "", last_name: "" }))} className={inp} data-testid="pe-name" /></Field>
                                <Field label="First name"><input value={f.first_name || autoSplit(f.name)[0]} onChange={(e) => setF((st) => { const ln = st.last_name || autoSplit(st.name)[1]; return { ...st, first_name: e.target.value, last_name: ln, name: `${e.target.value} ${ln}`.trim() }; })} className={inp} data-testid="pe-first-name" /></Field>
                                <Field label="Last name"><input value={f.last_name || autoSplit(f.name)[1]} onChange={(e) => setF((st) => { const fn = st.first_name || autoSplit(st.name)[0]; return { ...st, first_name: fn, last_name: e.target.value, name: `${fn} ${e.target.value}`.trim() }; })} className={inp} data-testid="pe-last-name" /></Field>
                                <NameCorrector name={f.name} first={f.first_name} onSplit={(i) => setF((st) => { const w = st.name.trim().split(/\s+/).filter(Boolean); return { ...st, first_name: w.slice(0, i).join(" "), last_name: w.slice(i).join(" ") }; })} />
                            </div>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
                                <Field label="League"><select value={f.league} onChange={(e) => setF((s) => ({ ...s, league: e.target.value, team_id: e.target.value === "DNP" ? "" : s.team_id }))} className={inp} data-testid="pe-league">{leagueOpts.map((lg) => <option key={lg} value={lg}>{lg === "DNP" ? "Inactive" : lg}</option>)}</select></Field>
                                <Field label="Team"><TeamPicker league={f.league} value={f.team_id} onChange={(v) => upd("team_id", v)} /></Field>
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-[10px] uppercase tracking-wider font-bold text-white/45">Position</span>
                                        <label className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-white/50 cursor-pointer whitespace-nowrap" title="Override auto position from PWHL game logs">
                                            <input type="checkbox" checked={f.position_manual} data-testid="pe-position-override"
                                                onChange={(e) => setF((s) => ({ ...s, position_manual: e.target.checked, position: e.target.checked ? (s.position || autoPos || "F") : (autoPos || s.position) }))} />
                                            Override
                                        </label>
                                    </div>
                                    <select value={f.position_manual ? f.position : (autoPos || f.position)} onChange={set("position")} disabled={!f.position_manual}
                                        className={inp + (f.position_manual ? "" : " opacity-60 cursor-not-allowed")} data-testid="pe-position">
                                        <option value="">—</option>{POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                    {!f.position_manual && <p className="text-[9px] text-white/40 mt-0.5" data-testid="pe-position-auto-note">Auto{autoPos ? `: ${autoPos}` : ""}</p>}
                                </div>

                                <Field label="Jersey #"><input value={f.jersey} onChange={set("jersey")} className={inp} data-testid="pe-jersey" /></Field>
                                <Field label="Shoots"><select value={f.shoots} onChange={set("shoots")} className={inp} data-testid="pe-shoots"><option value="">—</option><option value="L">L</option><option value="R">R</option></select></Field>
                                <div>
                                    <Field label="Height (cm)"><input value={f.height_cm} onChange={set("height_cm")} className={inp} data-testid="pe-height" /></Field>
                                    {f.height_cm ? <p className="text-[9px] text-white/40 mt-0.5" data-testid="pe-height-hint">{f.height_cm} cm · {cmToFtIn(f.height_cm)}</p> : null}
                                </div>
                                <div>
                                    <span className={lbl}>Date of birth</span>
                                    <DatePicker value={f.birthdate || ""} onChange={(v) => upd("birthdate", v)} editable testId="pe-dob" placeholder="Select date" />
                                    {ageFrom(f.birthdate) != null ? <p className="text-[9px] text-white/40 mt-0.5" data-testid="pe-age-hint">Age {ageFrom(f.birthdate)}</p> : null}
                                </div>

                                <Field label="Nationality"><input value={f.nationality} onChange={set("nationality")} placeholder="USA" className={inp} data-testid="pe-nat" /></Field>
                                <Field label="Birthplace"><input value={f.birthplace} onChange={set("birthplace")} className={inp} data-testid="pe-birthplace" /></Field>
                                <div className="col-span-2">
                                    <span className={lbl}>Photo</span>
                                    <ImageUpload value={f.photo_url} onChange={(v) => upd("photo_url", v)} size={56} testId="pe-photo" />
                                </div>
                            </div>

                            {f.photo_url && (
                                <PhotoCropAdjust photoUrl={f.photo_url} x={f.photo_x ?? 50} y={f.photo_y ?? 50} zoom={f.photo_zoom ?? 1} onChange={(patch) => setF((s) => ({ ...s, ...patch }))} />
                            )}
                        </div>
                    )}

                    {section === "logs" && (
                        <div className="space-y-5" data-testid="player-editor-logs">
                            <div data-testid="pe-logs-pwhl">
                                <p className={lbl}>PWHL game logs ({(f.pwhl || []).length})</p>
                                <SeasonsEditor title="" league="pwhl" isGoalie={isGoalie} rows={f.pwhl} expansionNow={expansionNow} onRowsChange={(next) => upd("pwhl", next)} />
                            </div>
                            <div data-testid="pe-logs-ncaa">
                                <p className={lbl}>College / NCAA ({(f.ncaa || []).length})</p>
                                <SeasonsEditor title="" league="ncaa" isGoalie={isGoalie} rows={f.ncaa} fullyEditable onRowsChange={(next) => upd("ncaa", next)} />
                            </div>
                            <div data-testid="pe-logs-intl">
                                <p className={lbl}>International ({(f.international || []).length})</p>
                                <SeasonsEditor title="" league="international" isGoalie={isGoalie} rows={f.international} fullyEditable onRowsChange={(next) => upd("international", next)} />
                            </div>
                            <div data-testid="pe-logs-other">
                                <p className={lbl}>Other leagues ({(f.other || []).length})</p>
                                <SeasonsEditor title="" league="other" isGoalie={isGoalie} rows={f.other || []} fullyEditable onRowsChange={(next) => upd("other", next)} />
                            </div>
                        </div>
                    )}

                    {section === "contracts" && (
                        <div className="space-y-3" data-testid="player-editor-contracts">
                            <p className={lbl}>Contract history ({groupedContracts.length})</p>
                            {groupedContracts.length === 0 ? <p className="text-xs text-white/40 italic">No contract records.</p> : (
                                <div className="rounded-lg border border-white/10 overflow-x-auto">
                                    <table className="w-full text-left text-xs whitespace-nowrap">
                                        <thead><tr className="bg-white/[0.03] text-white/40 uppercase tracking-wider text-[10px]">
                                            <th className="px-3 py-2">Team</th><th className="px-3 py-2">Term</th><th className="px-3 py-2">Seasons</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Salary</th><th className="px-3 py-2">Signed</th>
                                        </tr></thead>
                                        <tbody>
                                            {groupedContracts.map((c, i) => (
                                                <tr key={i} className="border-t border-white/[0.05] text-white/80" data-testid={`pe-contract-${i}`}>
                                                    <td className="px-3 py-2"><span className="inline-flex items-center gap-1.5">{TEAM_INFO[c.team_id] && <TeamBadge teamId={c.team_id} size="xs" asLink={false} />}{TEAM_INFO[c.team_id]?.abbr || (c.team_id || "—").toUpperCase()}</span></td>
                                                    <td className="px-3 py-2">{c.term || "—"}</td>
                                                    <td className="px-3 py-2 font-mono-stat">{c.seasons.length ? (c.seasons.length > 1 ? `${c.seasons[0]} → ${c.seasons[c.seasons.length - 1]}` : c.seasons[0]) : (c.season || "—")}</td>
                                                    <td className="px-3 py-2">{c.status || "—"}</td>
                                                    <td className="px-3 py-2 font-mono-stat">{c.salary ? `$${Number(c.salary).toLocaleString()}` : "—"}</td>
                                                    <td className="px-3 py-2 text-white/55">{c.date_signed || "—"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            <p className="text-[11px] text-white/35">Contracts are imported automatically. Each row is one signed deal (seasons combined); a player may have multiple deals in the same year.</p>
                        </div>
                    )}

                    {section === "history" && (
                        <div className="space-y-4" data-testid="player-editor-history">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <Field label="Entry method">
                                    {isDraft ? (
                                        <div className="w-full bg-[#0A0B10] border border-[#1E2233] rounded-md text-white text-sm px-2.5 py-1.5 flex items-center gap-2" data-testid="pe-entry-locked">
                                            <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded bg-[#9D4CDD]/25 text-[#C9A6FF] tracking-wider">Drafted</span>
                                            {draft ? (draft.year ? `${draft.year} PWHL Draft · Round ${draft.round}, Pick ${draft.pick}` : `Draft · ${draft.raw}`) : "Entry Draft"}
                                        </div>
                                    ) : (
                                        <select value={f.entry_method} onChange={set("entry_method")} className={inp} data-testid="pe-entry">
                                            <option value="">—</option>
                                            <option value="Free Agent">Free Agent Signing</option>
                                            <option value="Inaugural Signing">Inaugural Signing</option>
                                            <option value="Other">Other (specify)</option>
                                        </select>
                                    )}
                                </Field>
                                {!isDraft && f.entry_method === "Other" && <Field label="Specify"><input value={f.entry_specify} onChange={set("entry_specify")} placeholder="e.g. Expansion Draft" className={inp} data-testid="pe-entry-specify" /></Field>}
                                <Field label="Hall of Fame"><select value={f.hall_of_fame ? "1" : "0"} onChange={(e) => setF((s) => ({ ...s, hall_of_fame: e.target.value === "1" }))} className={inp} data-testid="pe-hof"><option value="0">No</option><option value="1">Yes</option></select></Field>
                                <Field label="Staff"><select value={f.is_staff ? "1" : "0"} onChange={(e) => setF((s) => ({ ...s, is_staff: e.target.value === "1" }))} className={inp} data-testid="pe-staff" title="STAFF: a staff member (coach/GM/etc.) for this team — never shown on rosters, lineups or leaderboards"><option value="0">Player</option><option value="1">STAFF</option></select></Field>
                                {f.is_staff && <Field label="Staff Role"><input value={f.staff_role} onChange={set("staff_role")} placeholder="e.g. Assistant Coach" className={inp} data-testid="pe-staff-role" /></Field>}
                            </div>
                            <p className="text-[11px] text-white/35 italic" data-testid="pe-history-note">Statistical history and awards are managed automatically through the system (Game Centre, contracts and PWHL logs). Imported third-party history has been removed.</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center gap-2 px-5 py-3 border-t border-white/[0.06]">
                    <div className="sm:hidden"><ChecklistBar p={player} f={f} /></div>
                    {isAdmin && !isNew && (
                        delOpen ? (
                            <div className="flex items-center gap-2 flex-wrap" data-testid="pe-delete-confirm">
                                <span className="text-[11px] font-bold text-[#F87171] uppercase tracking-wider">Type name to delete:</span>
                                <input value={delText} onChange={(e) => setDelText(e.target.value)} placeholder={player.name} autoFocus data-testid="pe-delete-input"
                                    className="bg-[#0A0B10] border border-[#EF4444]/50 rounded-md text-white text-sm px-2.5 py-1.5 outline-none focus:border-[#EF4444] w-52" />
                                <button onClick={() => del.mutate()} disabled={delText.trim() !== (player.name || "").trim() || del.isPending} data-testid="pe-delete-confirm-btn"
                                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold bg-[#EF4444] text-white hover:bg-[#DC2626] disabled:opacity-40">
                                    <Trash2 className="h-4 w-4" /> {del.isPending ? "Deleting…" : "Delete permanently"}
                                </button>
                                <button onClick={() => { setDelOpen(false); setDelText(""); }} className="text-white/40 hover:text-white text-sm">Cancel</button>
                            </div>
                        ) : (
                            <button onClick={() => setDelOpen(true)} data-testid="pe-delete-btn"
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold text-[#F87171] hover:bg-[#EF4444]/10">
                                <Trash2 className="h-4 w-4" /> Delete player
                            </button>
                        )
                    )}
                    <div className="ml-auto flex items-center gap-2">
                        {isNew && createConfirm ? (
                            <>
                                <span className="text-[11px] font-bold text-[#86EFAC] uppercase tracking-wider">Create this player?</span>
                                <button onClick={() => setCreateConfirm(false)} className="px-4 py-2 rounded-lg text-sm text-white/60 hover:text-white">Cancel</button>
                                <button onClick={() => save.mutate()} disabled={save.isPending} data-testid="player-editor-create-confirm"
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-[#22C55E] text-black hover:bg-[#16A34A] disabled:opacity-40">
                                    <Save className="h-4 w-4" /> {save.isPending ? "Creating…" : "Confirm — create"}
                                </button>
                            </>
                        ) : (
                            <>
                                <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-white/60 hover:text-white">Cancel</button>
                                <button onClick={() => (isNew ? setCreateConfirm(true) : save.mutate())} disabled={save.isPending || (isNew && !(f.name || "").trim())} data-testid="player-editor-save"
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-[#9D4CDD] text-white hover:bg-[#8B3FC9] disabled:opacity-40">
                                    <Save className="h-4 w-4" /> {save.isPending ? "Saving…" : (isNew ? "Create player" : "Save player")}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
